/**
 * Location Service
 * Handles geolocation requests and location validation
 */

const API_BASE_URL = '/api';

export interface LocationCoordinates {
    latitude: number;
    longitude: number;
    accuracy: number;
}

export interface LocationValidationResult {
    allowed: boolean;
    distance: number | null;
    message: string;
}

/**
 * Get user's current location using browser Geolocation API
 * @returns Promise with location coordinates
 */
export const getCurrentLocation = (): Promise<LocationCoordinates> => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported by your browser'));
            return;
        }

        const options = {
            enableHighAccuracy: true,
            timeout: 8000,
            maximumAge: 10000 // Allow up to 10 seconds cached location for instant load
        };

        const successCallback = (position: GeolocationPosition) => {
            resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy
            });
        };

        const errorCallback = (error: GeolocationPositionError) => {
            // Fallback: if high accuracy failed or timed out, try standard accuracy
            if (options.enableHighAccuracy) {
                console.warn('High accuracy location failed. Falling back to low accuracy...');
                options.enableHighAccuracy = false;
                options.timeout = 10000;
                navigator.geolocation.getCurrentPosition(
                    successCallback,
                    (lowAccErr) => {
                        let errorMessage = 'Failed to get location';
                        switch (lowAccErr.code) {
                            case lowAccErr.PERMISSION_DENIED:
                                errorMessage = 'Location permission denied. Please enable location access in settings.';
                                break;
                            default:
                                errorMessage = 'Location request timed out. Make sure GPS/location services are enabled.';
                                break;
                        }
                        reject(new Error(errorMessage));
                    },
                    options
                );
            } else {
                let errorMessage = 'Failed to get location';
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'Location permission denied. Please enable location access in settings.';
                        break;
                    default:
                        errorMessage = 'Location unavailable or timed out.';
                        break;
                }
                reject(new Error(errorMessage));
            }
        };

        navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);
    });
};

/**
 * Validate location coordinates against office location via backend API
 * @param latitude User's latitude
 * @param longitude User's longitude
 * @returns Promise with validation result
 */
export const validateLocation = async (
    latitude: number,
    longitude: number
): Promise<LocationValidationResult> => {
    try {
        const response = await fetch(`${API_BASE_URL}/validate-location`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ latitude, longitude })
        });

        if (!response.ok) {
            throw new Error('Failed to validate location');
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Location validation error:', error);
        throw new Error('Failed to validate location. Please try again.');
    }
};

/**
 * Check if user is at office location
 * Combines getCurrentLocation and validateLocation
 * @returns Promise with validation result
 */
export const checkOfficeLocation = async (): Promise<LocationValidationResult> => {
    const location = await getCurrentLocation();
    const result = await validateLocation(location.latitude, location.longitude);
    return result;
};
